import { useReportExportContext } from "./useReportExportContext";
import * as logger from "common/logger";
import { useCallback, useState } from "react";
import { useRunImmediateReportMutation } from "services/reportsV3Service";
import { plainFetch } from "utils";

// The service answers with a presigned S3 URL whenever the XLSX is too large to
// inline or the run was backgrounded. A presigned URL carries its own signature
// in the query string, so it must be fetched *without* our Authorization
// header: S3 rejects a request holding both with 400 "Only one auth mechanism
// allowed".
// The JSON run path fetches report data the same way (see extractReadyData in
// services/reportsV3ServiceHelpers.js).
const downloadFromUrl = async (url, filename) => {
  const fileResponse = await plainFetch(url);
  const blob = await fileResponse.blob();
  triggerDownload(blob, filename);
};

const downloadFromBase64 = (data, filename) => {
  const byteCharacters = atob(data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  triggerDownload(blob, filename);
};

const triggerDownload = (blob, filename) => {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

// Failures arrive in three shapes: an RTK error payload from .unwrap()
// ({ status, data }), a HandledError from plainFetch (message on .description),
// or a plain Error. Reduce them to something a tooltip can show, so a failed
// download is never silent.
export const downloadErrorMessage = (error) => {
  if (typeof error?.data === "string" && error.data.length > 0) {
    return error.data;
  }
  return error?.description || error?.message || "Failed to download XLSX";
};

export const useXlsxDownload = ({ reportName, buildReportData }) => {
  const [
    runImmediateReport,
    { isLoading: downloadLoading },
  ] = useRunImmediateReportMutation();
  const [downloadError, setDownloadError] = useState(null);

  // Optional "as at" date lifted into ReportExportContext (set via the field
  // beside the Run button). Blank/none means no as-at is sent. The download
  // must honour it so an XLSX export matches the on-screen, as-at results.
  const { asAt } = useReportExportContext() ?? {};

  const handleDownloadXLSX = useCallback(async () => {
    setDownloadError(null);
    try {
      const report = buildReportData();
      // BY_URL backgrounds the run straight away, so neither the run nor the
      // workbook render races the web Lambda's timeout, and the run happens
      // once rather than inline and then again in the background. The
      // mutation polls for READY and hands back the presigned URL.
      const data = await runImmediateReport({
        report,
        outputType: "XLSX",
        presentationType: "BY_URL",
        ...(asAt ? { asAt } : {}),
      }).unwrap();

      const filename = `${reportName || "report"}.xlsx`;
      if (data.url) {
        await downloadFromUrl(data.url, filename);
      } else if (data.data) {
        downloadFromBase64(data.data, filename);
      } else {
        throw new Error("Unexpected response format");
      }
    } catch (error) {
      logger.exception(error, { breadcrumb: "Failed to download XLSX" });
      setDownloadError(downloadErrorMessage(error));
    }
  }, [reportName, buildReportData, runImmediateReport, asAt]);

  return {
    downloadLoading,
    downloadError,
    handleDownloadXLSX,
  };
};
